import { useCallback, useEffect, useMemo, useState } from "react";

import { datadogRum } from "@datadog/browser-rum";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { useAppLanguageStore } from "app/stores/useAppLanguageStore";
import { routes } from "config/routes";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { IntlMessageId } from "shared/lang";
import { Category, TranslatedCategory } from "shared/models/category/types";
import { serializeProductsInSubcategory } from "shared/models/product/serializer";
import { TranslatedProductBySubcategory } from "shared/models/product/types";
import { TranslatedSubcategory } from "shared/models/subcategory/types";
import { useGetNonScannableCategoriesLazyQuery } from "shared/queries/category/category.generated";
import { useGetProductsBySubcategoryV2LazyQuery } from "shared/queries/product/product.generated";
import { LetterRange } from "ui/LetterRangeSelector/LetterRangeSelector";
import { countryNameFromCode } from "utils/countryNames";
import { getFetchPolicyWithCacheTimeout } from "utils/graphql/graphql";
import { isNullOrUndefined } from "utils/tsHelpers";

import { ProductSearchActionTypes, useProductSearchStore } from "./useProductSearchStore";

const GET_PRODUCTS_BY_SUBCATEGORY_ERROR_TOAST_ID = "get_products_by_subcategory_error_toast_id";
const GET_NON_SCANNABLE_CATEGORIES_ERROR_TOAST_ID = "get_non_scannable_categories_error_toast_id";

const letterRangeTemplates: Omit<LetterRange, "disabled">[] = [
  { id: "AD", from: "A", to: "D" },
  { id: "EH", from: "E", to: "H" },
  { id: "IL", from: "I", to: "L" },
  { id: "MP", from: "M", to: "P" },
  { id: "QT", from: "Q", to: "T" },
  { id: "UX", from: "U", to: "X" },
  { id: "YZ", from: "Y", to: "Z" },
];

function normalizeCategoryNameForPhraseKey(name: string): string {
  return name
    .replace(/[^a-zA-Z\s]/g, "")
    .toLowerCase()
    .replace(/\s/g, "-");
}

function nextLetter(letter: string): string {
  const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  return ALPHABET[ALPHABET.indexOf(letter) + 1];
}

interface UseFetchCategoriesState {
  categories: Record<string, TranslatedSubcategory[]> | null;
  isLoading: boolean;
}

interface UseFetchProductsBySubcategory {
  products: TranslatedProductBySubcategory[] | null;
  isLoading: boolean;
}

export function useTranslatedNonScannableCategories() {
  const intl = useIntl();
  return useCallback(
    (categories: Category[] | null): TranslatedCategory[] | null => {
      if (isNullOrUndefined(categories)) return null;

      return categories.map((category) => ({
        ...category,
        subcategories: category.subcategories.map((subcategory) => ({
          ...subcategory,
          translatedName: intl.formatMessage({
            id: `flows.inbound.no-ean-search.subcategories.${normalizeCategoryNameForPhraseKey(
              category.name,
            )}.${normalizeCategoryNameForPhraseKey(subcategory.name)}` as IntlMessageId,
            defaultMessage: subcategory.name,
          }),
        })),
      }));
    },
    [intl],
  );
}

export function useSerialiseAndTranslatedProducts() {
  const intl = useIntl();
  return useCallback(
    (categories: Category[] | null): TranslatedCategory[] | null => {
      if (isNullOrUndefined(categories)) return null;

      return categories.map((category) => ({
        ...category,
        subcategories: category.subcategories.map((subcategory) => ({
          ...subcategory,
          translatedName: intl.formatMessage({
            id: `flows.inbound.no-ean-search.subcategories.${normalizeCategoryNameForPhraseKey(
              category.name,
            )}.${normalizeCategoryNameForPhraseKey(subcategory.name)}` as IntlMessageId,
            defaultMessage: subcategory.name,
          }),
        })),
      }));
    },
    [intl],
  );
}

export function useFilteredSortedGroupedAndTranslatedSubcategories() {
  const appLanguage = useAppLanguageStore((state) => state.appLanguage);
  const { selectedCategoryName } = useProductSearchStore(
    useShallow((state) => ({ selectedCategoryName: state.selectedCategoryName })),
  );

  return useCallback(
    (translatedCategories: TranslatedCategory[] | null): UseFetchCategoriesState["categories"] => {
      if (isNullOrUndefined(translatedCategories) || isNullOrUndefined(selectedCategoryName))
        return null;

      const selectedCategory = translatedCategories.find(
        (category) => category.name === selectedCategoryName,
      );
      const filteredSubcategories = (selectedCategory?.subcategories || []).filter(
        (subcat) => subcat.productsCount > 0,
      );

      const { compare } = new Intl.Collator(appLanguage);
      const sortedSubcategories = filteredSubcategories.sort((s1, s2) =>
        compare(s1.translatedName, s2.translatedName),
      );

      return letterRangeTemplates.reduce<Record<string, TranslatedSubcategory[]>>(
        (subCatGroups, letterRange) => {
          subCatGroups[letterRange.id] = sortedSubcategories.filter(
            (s) =>
              compare(letterRange.from, s.translatedName) === -1 &&
              (isNullOrUndefined(nextLetter(letterRange.to)) ||
                compare(nextLetter(letterRange.to), s.translatedName) === 1),
          );
          return subCatGroups;
        },
        {},
      );
    },
    [selectedCategoryName, appLanguage],
  );
}

export function useLetterRanges(groupedSubcategories: Record<string, TranslatedSubcategory[]>) {
  return useMemo(
    () =>
      letterRangeTemplates.map((template) => ({
        ...template,
        disabled: groupedSubcategories[template.id]?.length === 0,
      })),
    [groupedSubcategories],
  );
}

export function useSafeSelectedLetterRangeId(
  letterRanges: LetterRange[],
  selectedLetterRangeId: string,
) {
  return useMemo(() => {
    const isSelectedRangeDisabled = letterRanges.find(
      (lr) => lr.id === selectedLetterRangeId,
    )?.disabled;
    if (!isSelectedRangeDisabled) return selectedLetterRangeId;

    const firstNonDisabledRange = letterRanges.find((lr) => !lr.disabled);
    return firstNonDisabledRange?.id || selectedLetterRangeId;
  }, [letterRanges, selectedLetterRangeId]);
}

export function useFetchNonScannableCategories() {
  const { showToastUI } = useCustomToast();
  const { resetTextSearch } = useProductSearchStore(
    useShallow((state) => ({
      resetTextSearch: state.resetTextSearch,
    })),
  );
  const [state, setState] = useState<UseFetchCategoriesState>({
    categories: null,
    isLoading: false,
  });

  const [translatedCategories, setTranslatedCategories] = useState<TranslatedCategory[] | null>(
    null,
  );

  const handleFetchError = useCallback(
    (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      datadogRum.addError("Error while fetching non-scannable subcategories", { errorMessage });
      showToastUI({
        id: GET_NON_SCANNABLE_CATEGORIES_ERROR_TOAST_ID,
        title: "get_non_scannable_categories_error_toast_id",
      });
      resetTextSearch();
    },
    [resetTextSearch, showToastUI],
  );
  const [getNonScannableCategories] = useGetNonScannableCategoriesLazyQuery({
    fetchPolicy: getFetchPolicyWithCacheTimeout({ key: "getNonScannableCategories", minutes: 60 }),
    onError: handleFetchError,
  });

  const translateCategories = useTranslatedNonScannableCategories();
  const filterSubcategories = useFilteredSortedGroupedAndTranslatedSubcategories();

  const fetchCategories = useCallback(async () => {
    setState((prevState) => ({ ...prevState, isLoading: true }));

    try {
      const { data } = await getNonScannableCategories();
      if (data?.getNonScannableCategories?.categories) {
        const translated = translateCategories(data.getNonScannableCategories.categories);
        setTranslatedCategories(translated);
      } else {
        setTranslatedCategories(null);
      }
    } catch (error) {
      handleFetchError(error);
    } finally {
      setState((prevState) => ({ ...prevState, isLoading: false }));
    }
  }, [getNonScannableCategories, handleFetchError, translateCategories]);

  useEffect(() => {
    if (translatedCategories) {
      const groupedSubcategories = filterSubcategories(translatedCategories);
      setState((prevState) => ({ ...prevState, categories: groupedSubcategories }));
    }
  }, [translatedCategories, filterSubcategories]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return state;
}

export function useFetchProductsBySubcategory() {
  const navigate = useNavigate();
  const { showToastUI } = useCustomToast();
  const appLanguage = useAppLanguageStore((state) => state.appLanguage);
  const { selectedSubcategoryID, setSearchState } = useProductSearchStore(
    useShallow((state) => ({
      selectedSubcategoryID: state.selectedSubcategoryID,
      setSearchState: state.setSearchState,
    })),
  );

  const [state, setState] = useState<UseFetchProductsBySubcategory>({
    products: null,
    isLoading: false,
  });

  const handleFetchError = useCallback(
    (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      datadogRum.addError("Error while fetching products by subcategory", { errorMessage });
      showToastUI({
        id: GET_PRODUCTS_BY_SUBCATEGORY_ERROR_TOAST_ID,
        title: "get_products_by_subcategory_error_toast_id",
      });
      setSearchState(ProductSearchActionTypes.ACTIVATE_CATEGORY_SEARCH);
      navigate(routes.inbound.manualSearch);
    },
    [navigate, setSearchState, showToastUI],
  );

  const [getProductsBySubcategory] = useGetProductsBySubcategoryV2LazyQuery({
    fetchPolicy: "no-cache",
    onError: handleFetchError,
  });

  const fetchProductsBySubcategory = useCallback(async () => {
    if (selectedSubcategoryID) {
      setState((prevState) => ({ ...prevState, isLoading: true }));
      try {
        const { data } = await getProductsBySubcategory({
          variables: {
            input: {
              subcategoryId: selectedSubcategoryID,
            },
          },
        });
        if (data) {
          const serialized = serializeProductsInSubcategory(data);
          const translated = serialized?.map((product) => ({
            ...product,
            countryOfOriginName:
              countryNameFromCode(appLanguage, product.countryOfOriginCode) ?? null,
          }));
          setState((prevState) => ({ ...prevState, products: translated }));
        } else {
          setState((prevState) => ({ ...prevState, products: null }));
        }
      } catch (error) {
        handleFetchError(error);
      } finally {
        setState((prevState) => ({ ...prevState, isLoading: false }));
      }
    }
  }, [appLanguage, getProductsBySubcategory, handleFetchError, selectedSubcategoryID]);

  useEffect(() => {
    fetchProductsBySubcategory();
  }, [fetchProductsBySubcategory]);

  return state;
}
